import {motion} from 'framer-motion';
import {ReactNode, useMemo} from 'react';
import {observer} from 'mobx-react-lite';
import styled, {css, useTheme} from 'styled-components';
import {useStoreContext} from '@store/store-context';
import {NotificationType, TNotificationProps} from '@ui/interface';
import {CancelIcon} from '@ui/atoms/icons/cancel';
import {AlertBadgeSuccessSVG} from '@ui/atoms/svg/alert-badge-success';
import {AlertBadgeInfoSVG} from '@ui/atoms/svg/alert-badge-info';
import {AlertBadgeErrorSVG} from '@ui/atoms/svg/alert-badge-error';
import {AlertBadgeWarningSVG} from '@ui/atoms/svg/alert-badge-warning';

const statusColor = (kind: NotificationType, colors: any) => {
  switch (kind) {
    case 'success':
      return colors.green400;
    case 'error':
      return colors.red400;
    case 'warning':
      return colors.orange300;
    default:
      return colors.purple300;
  }
};

const ToastRegion = styled.div`
  display: flex;
  justify-content: flex-end;
  left: 0;
  padding: 16px 24px;
  pointer-events: none;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1200;

  @media (max-width: 767px) {
    top: 56px;
  }

  @media (max-width: 576px) {
    padding: 12px;
  }
`;

const Toast = styled(motion.div)<{$kind: NotificationType}>`
  align-items: flex-start;
  background: ${({theme}) => theme.colors.white};
  border: 1px solid ${({theme}) => theme.colors.borderLight};
  border-left: 4px solid ${({$kind, theme}) => statusColor($kind, theme.colors)};
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(36, 36, 36, 0.14);
  display: grid;
  gap: 12px;
  grid-template-columns: 32px minmax(0, 1fr) 28px;
  max-width: 420px;
  padding: 14px 12px 14px 14px;
  pointer-events: auto;
  width: 100%;
`;

const StatusIcon = styled.span<{$kind: NotificationType}>`
  align-items: center;
  background: ${({$kind, theme}) => statusColor($kind, theme.colors)};
  border-radius: 50%;
  display: inline-flex;
  height: 32px;
  justify-content: center;
  width: 32px;

  svg {
    height: 18px;
    width: 18px;
  }

  path {
    stroke: ${({theme}) => theme.colors.alwaysWhite};
  }
`;

const ToastCopy = styled.div`
  min-width: 0;
  padding-top: 1px;
`;

const ToastTitle = styled.div`
  color: ${({theme}) => theme.colors.blackTwo};
  font: 600 14px/20px Graphik, sans-serif;
`;

const ToastContent = styled.div`
  color: ${({theme}) => theme.colors.textBody};
  font: 400 13px/19px Graphik, sans-serif;
  margin-top: 2px;
  overflow-wrap: anywhere;
`;

const ToastAction = styled.button`
  background: none;
  border: 0;
  color: ${({theme}) => theme.colors.purple300};
  cursor: pointer;
  font: 500 13px/20px Graphik, sans-serif;
  margin-top: 8px;
  padding: 0;
`;

const CloseButton = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  height: 28px;
  justify-content: center;
  padding: 0;
  width: 28px;

  &:hover {
    background: ${({theme}) => theme.colors.backgroundDark};
  }
`;

const SecondaryWrapper = styled.div<{hide: boolean}>`
  align-items: center;
  background: ${({theme}) => theme.colors.purple300};
  color: ${({theme}) => theme.colors.alwaysWhite};
  display: flex;
  justify-content: center;
  left: 0;
  padding: 10px 16px;
  position: fixed;
  right: 0;
  top: 75px;
  width: 100vw;
  z-index: 2;

  .action {
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    line-height: 22px;
    margin-left: 5px;
    text-decoration: underline;
  }

  ${({hide}) =>
    hide &&
    css`
      display: none;
    `}
`;

export type NotificationProps = TNotificationProps & {onDismiss?: () => void};

export const Notification = (props: NotificationProps) => {
  const activeTheme = useTheme() as any;
  const kind = props.kind || 'info';
  const dismiss = props.hideNotification || props.onDismiss;
  const icon = useMemo(() => {
    switch (kind) {
      case 'success':
        return <AlertBadgeSuccessSVG />;
      case 'error':
        return <AlertBadgeErrorSVG />;
      case 'warning':
        return <AlertBadgeWarningSVG />;
      default:
        return <AlertBadgeInfoSVG />;
    }
  }, [kind]);

  return (
    <ToastRegion
      aria-live={
        kind === 'error' || kind === 'warning' ? 'assertive' : 'polite'
      }>
      <Toast
        $kind={kind}
        role={kind === 'error' || kind === 'warning' ? 'alert' : 'status'}
        initial={{opacity: 0, x: 16, y: -8}}
        animate={{opacity: 1, x: 0, y: 0}}
        exit={{opacity: 0, x: 12}}
        transition={{duration: 0.18}}>
        <StatusIcon $kind={kind}>{icon}</StatusIcon>
        <ToastCopy>
          <ToastTitle>{props.title}</ToastTitle>
          <ToastContent>{props.content as ReactNode}</ToastContent>
          {props.action && (
            <ToastAction type="button" onClick={props.action.onClick}>
              {props.action.title}
            </ToastAction>
          )}
        </ToastCopy>
        <CloseButton
          type="button"
          aria-label="Dismiss notification"
          onClick={dismiss}>
          <CancelIcon color={activeTheme.colors.textBody} />
        </CloseButton>
      </Toast>
    </ToastRegion>
  );
};

export const NotificationCard = observer(() => {
  const {
    notificationStore: {
      notificationData,
      showNotification,
      appIsOffline,
      handleShowNotification,
    },
  } = useStoreContext();

  if (!showNotification) return null;

  return (
    <Notification
      {...notificationData}
      hideNotification={() =>
        appIsOffline ? undefined : handleShowNotification(false)
      }
    />
  );
});

interface NotifySecondary {
  title: string | ReactNode;
  action?: {
    title: string;
    onClick: () => void;
  };
}

export const NotificationSecondary = observer(
  ({title, action}: NotifySecondary) => {
    const {
      notificationStore: {showNotification},
    } = useStoreContext();

    return (
      <SecondaryWrapper hide={showNotification}>
        {title}
        {action && (
          <button className="action" type="button" onClick={action.onClick}>
            {action.title}
          </button>
        )}
      </SecondaryWrapper>
    );
  },
);
